import { createApi } from '@reduxjs/toolkit/query/react';
import customBaseQuery from '@/lib/customBaseQuery';

const organizationBaseUrl = '/api/organizations';
export const organizationApi = createApi({
  reducerPath: 'organizationApi',
  baseQuery: customBaseQuery,
  tagTypes: ['Organization', 'OrganizationList'],
  endpoints: (builder) => ({
    allOrganizations: builder.query<OrganizationsTableResponse, OrganizationSearchParams>({
      query: (params) => ({
        url: `${organizationBaseUrl}/`,
        params,
      }),
      providesTags: ['OrganizationList'],
    }),
    cityOptions: builder.query<OptionSelect[], void>({
      query: () => ({
        url: `${organizationBaseUrl}/city-options`,
      }),
    }),
    facilityOptions: builder.query<OptionSelect[], void>({
      query: () => ({
        url: `${organizationBaseUrl}/facility-options`,
      }),
    }),
    getMagicLink: builder.query<{ jwtToken: string }, number>({
      query: (id) => ({
        url: `${organizationBaseUrl}/magic-link/${id}`,
      }),
    }),
    orgById: builder.query<OrganizationByIdResponse, { id: number; accessToken?: string | null }>({
      query: ({ id, accessToken }) => ({
        url: `${organizationBaseUrl}/info/${id}`,
        params: accessToken ? { access_token: accessToken } : undefined,
      }),
      providesTags: (_result, _error, { id }) => [{ type: 'Organization', id }],
    }),
    saveOrganization: builder.mutation<OrganizationByIdResponse, PostOrganizationPayload>({
      query: (payload) => ({
        url: organizationBaseUrl,
        method: 'POST',
        params: payload?.accessToken ? { access_token: payload.accessToken } : undefined,
        body: payload,
      }),
      invalidatesTags: (_result, _error, payload) => [
        'OrganizationList',
        { type: 'Organization', id: payload.id ?? undefined },
      ],
    }),
    deleteOrganization: builder.mutation<string, number>({
      query: (id) => ({
        url: `${organizationBaseUrl}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        'OrganizationList',
        { type: 'Organization', id },
      ],
    }),
    requestUpdateInfo: builder.mutation<string, number>({
      query: (id) => ({
        url: `${organizationBaseUrl}/request-update-info/${id}`,
        method: 'PUT',
      }),
    }),
    blastEmailRequest: builder.mutation<string, BlastEmailPayload>({
      query: (body) => ({
        url: `${organizationBaseUrl}/blast-email`,
        method: 'POST',
        body,
      }),
    }),
    saveInventoryByOrgId: builder.mutation<OrganizationInventoryData, OrgInventoryPayload>({
      query: (payload) => ({
        url: `${organizationBaseUrl}/inventory`,
        method: 'POST',
        params: payload?.accessToken ? { access_token: payload.accessToken } : undefined,
        body: payload,
      }),
      invalidatesTags: (_result, _error, payload) => [
        { type: 'Organization', id: payload.facilityId },
      ],
    }),
    requestUpdateInventory: builder.mutation<string, number>({
      query: (id) => ({
        url: `${organizationBaseUrl}/request-update-inventory/${id}`,
        method: 'PUT',
      }),
    }),
    blastEmailInventory: builder.mutation<string, void>({
      query: () => ({
        url: `${organizationBaseUrl}/blast-inventory`,
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useAllOrganizationsQuery,
  useBlastEmailInventoryMutation,
  useBlastEmailRequestMutation,
  useCityOptionsQuery,
  useFacilityOptionsQuery,
  useLazyGetMagicLinkQuery,
  useOrgByIdQuery,
  useSaveOrganizationMutation,
  useSaveInventoryByOrgIdMutation,
  useDeleteOrganizationMutation,
  useRequestUpdateInfoMutation,
  useRequestUpdateInventoryMutation,
} = organizationApi;
